import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, TextField, Typography, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  GetCurrentTenantDocument,
  UpdateTenantResellerLogoDocument,
  type CreateOrganizationInput,
  type GetCurrentTenantQuery,
} from '@/generated/root-types';
import { useNotification } from '@/app/contexts/NotificationContext';
import { errorsFromPayload } from '@/utils/mutationErrors';
import {
  DEFAULT_ORDA_LOGO_SRC,
  MAX_LOGO_FILE_BYTES,
} from '@/components/customer-invite/customerInviteUtils';
import { saveSetupData, getSetupData } from '../utils/setupStorage';

interface OrganizationStepProps {
  onNext: () => void;
}

const OrganizationStep: React.FC<OrganizationStepProps> = ({ onNext }) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<Partial<CreateOrganizationInput>>({
    name: '',
    description: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: tenantData } = useQuery<GetCurrentTenantQuery>(GetCurrentTenantDocument);
  const tenantId = tenantData?.currentTenant?.id;
  const savedLogoFromServer = tenantData?.currentTenant?.resellerLogo ?? null;

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [savedLogo, setSavedLogo] = useState<string | null>(null);
  const [updateLogo, { loading: savingLogo }] = useMutation(UpdateTenantResellerLogoDocument);

  // Load existing organization data from localStorage
  useEffect(() => {
    const savedData = getSetupData<Partial<CreateOrganizationInput>>('organization');
    if (savedData) {
      setFormData({ name: savedData.name || '', description: savedData.description || '' });
    }
  }, []);

  useEffect(() => {
    setSavedLogo(savedLogoFromServer);
    setLogoPreview(savedLogoFromServer);
  }, [savedLogoFromServer]);

  const isLogoDirty = logoPreview !== savedLogo;

  const handleChange =
    (field: keyof CreateOrganizationInput) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: event.target.value }));
      if (errors[field]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    };

  const handleLogoSelect = (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showError(t('customers.invite.logoInvalidType'));
      return;
    }
    if (file.size > MAX_LOGO_FILE_BYTES) {
      showError(t('customers.invite.logoTooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : null;
      if (!dataUrl) return;
      setLogoPreview(dataUrl);
      void handleSaveLogo(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveLogo = async (logoDataUrl: string) => {
    if (!tenantId) return;
    try {
      const res = await updateLogo({
        variables: { input: { tenantId, resellerLogo: logoDataUrl } },
      });
      const payload = res.data?.updateTenantResellerLogo;
      const err = errorsFromPayload(payload);
      if (err) {
        showError(err);
        return;
      }
      const nextLogo = payload?.tenant?.resellerLogo ?? logoDataUrl;
      setSavedLogo(nextLogo);
      setLogoPreview(nextLogo);
      showSuccess(t('customers.invite.logoSaved'));
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name?.trim()) {
      newErrors.name = t('FieldRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validate()) {
      return;
    }
    saveSetupData('organization', { ...formData });
    onNext();
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3 }}>
        {t('setup.organization.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('setup.organization.description')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          p: 2,
          mb: 3,
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          component="img"
          src={logoPreview || DEFAULT_ORDA_LOGO_SRC}
          alt=""
          sx={{
            maxWidth: 160,
            maxHeight: 80,
            objectFit: 'contain',
            alignSelf: { xs: 'center', sm: 'flex-start' },
          }}
        />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          <Typography variant="subtitle2">{t('setup.organization.logoTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('setup.organization.logoHint')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              hidden
              onChange={(e) => handleLogoSelect(e.target.files?.[0] ?? null)}
            />
            <Button variant="outlined" size="small" onClick={() => fileInputRef.current?.click()}>
              {t('customers.invite.logoUpload')}
            </Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label={t('setup.organization.nameLabel')}
            value={formData.name || ''}
            onChange={handleChange('name')}
            required
            error={!!errors.name}
            helperText={errors.name}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label={t('setup.organization.descriptionLabel')}
            value={formData.description || ''}
            onChange={handleChange('description')}
            multiline
            rows={3}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" color="primary" onClick={handleNext}>
          {t('Next')}
        </Button>
      </Box>
    </Box>
  );
};

export default OrganizationStep;
